<template>
  <v-expansion-panel class="history-panel mb-3">
    <v-expansion-panel-title class="history-panel-title pa-4">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="mr-3">
          <v-avatar size="36" color="grey" class="text-white">
            <v-icon size="small" color="white">{{ historyItem.Job.StatusId === 100 ? 'mdi-check-all' : 'mdi-check'
              }}</v-icon>
          </v-avatar>
        </v-col>
        <v-col>
          <div class="d-flex align-center flex-wrap">
            <span class="text-subtitle-2 font-weight-bold mr-2" style="color: rgba(0,0,0,.87);">
              {{ historyItem.Job.OrderId || historyItem.ServiceHistoryOrderReference }}
            </span>
          </div>
          <div class="text-caption" style="color: rgba(0,0,0,.54);">
            {{ historyItem.Job.DisplayName }}
          </div>
        </v-col>
        <v-col cols="auto" class="text-right" :class="{ 'd-none': isPhone }">
          <div class="text-caption" style="color: rgba(0,0,0,.54);">
            {{ formatDate(historyItem.CreatedDate) }}
            <span v-if="historyItem.CompletedDate"> — {{ formatDate(historyItem.CompletedDate) }}</span>
          </div>
        </v-col>
      </v-row>
    </v-expansion-panel-title>

    <v-expansion-panel-text class="history-panel-content">
      <v-divider class="mb-3" />

      <v-row class="mb-3" v-if="authUserInfo && authUserInfo.IsJobTimelineEnabled">
        <v-col cols="12" sm="6" md="3" v-if="historyItem.CreatedDate">
          <div class="date-block">
            <div class="text-overline" style="line-height: 1.2;">{{ $t("text.registered") }}</div>
            <div class="text-body-2 font-weight-medium">{{ formatDate(historyItem.CreatedDate) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3" v-if="historyItem.PlannedDate">
          <div class="date-block">
            <div class="text-overline" style="line-height: 1.2;">{{ $t("text.scheduledfor") }}</div>
            <div class="text-body-2 font-weight-medium">{{ formatDate(historyItem.PlannedDate) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3" v-if="historyItem.CompletedDate">
          <div class="date-block">
            <div class="text-overline" style="line-height: 1.2;">Completed</div>
            <div class="text-body-2 font-weight-medium">{{ formatDate(historyItem.CompletedDate) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3" v-if="historyItem.ClosedDate">
          <div class="date-block">
            <div class="text-overline" style="line-height: 1.2;">{{ $t("text.closed") }}</div>
            <div class="text-body-2 font-weight-medium">{{ formatDate(historyItem.ClosedDate) }}</div>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" v-if="historyItem.CustomerComment || historyItem.Job.CustomerComment">
          <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
            {{ $t("text.customer_comment") }}
          </div>
          <div class="text-body-2 comment-text mt-1">
            {{ historyItem.CustomerComment || historyItem.Job.CustomerComment || '—' }}
          </div>
        </v-col>
        <v-col cols="12" sm="6" v-if="historyItem.InvoiceComment || historyItem.Job.InvoiceComment">
          <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
            {{ $t("text.invocie_comment") }}
          </div>
          <div class="text-body-2 comment-text mt-1">
            {{ historyItem.InvoiceComment || historyItem.Job.InvoiceComment || '—' }}
          </div>
        </v-col>
      </v-row>

      <div v-if="historyItem.Job.CustomerDeliveryAdressStreet || historyItem.Job.CustomerDeliveryAdressCity"
        class="mt-3">
        <div class="text-caption font-weight-medium" style="color: rgba(0,0,0,.54);">
          <v-icon size="x-small" class="mr-1">mdi-map-marker-outline</v-icon>
          {{ $t("text.delivery_address") }}
        </div>
        <div class="text-body-2 mt-1">
          {{ [historyItem.Job.CustomerDeliveryAdressName, historyItem.Job.CustomerDeliveryAdressStreet,
          historyItem.Job.CustomerDeliveryAdressZip, historyItem.Job.CustomerDeliveryAdressCity,
          historyItem.Job.CustomerDeliveryAdressCountry].filter(Boolean).join(', ') }}
        </div>
      </div>

      <div v-if="showPrice && hasCostData(historyItem)" class="mt-4">
        <div class="text-caption font-weight-medium mb-2" style="color: rgba(0,0,0,.54);">
          {{ $t("text.cost") }}
        </div>
        <v-row>
          <v-col cols="auto" v-if="historyItem.KPIWorkCost != null" class="mr-4">
            <v-card variant="outlined" class="pa-3 text-center cost-card">
              <div class="text-overline" style="line-height: 1.2;">{{ $t("text.Service") }}</div>
              <div class="text-subtitle-1 font-weight-bold text-primary">
                {{ formatCurrency(historyItem.KPIWorkCost) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="auto" v-if="historyItem.KPIPartCost != null" class="mr-4">
            <v-card variant="outlined" class="pa-3 text-center cost-card">
              <div class="text-overline" style="line-height: 1.2;">{{ $t("text.Parts") }}</div>
              <div class="text-subtitle-1 font-weight-bold text-primary">
                {{ formatCurrency(historyItem.KPIPartCost) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="auto" v-if="historyItem.KPIDrivingCost != null">
            <v-card variant="outlined" class="pa-3 text-center cost-card">
              <div class="text-overline" style="line-height: 1.2;">Driving</div>
              <div class="text-subtitle-1 font-weight-bold text-primary">
                {{ formatCurrency(historyItem.KPIDrivingCost) }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div v-if="historyItem.MachineServiceHistoriesParts && historyItem.MachineServiceHistoriesParts.length > 0"
        class="mt-4">
        <div class="text-caption font-weight-medium mb-2" style="color: rgba(0,0,0,.54);">
          {{ $t("text.articles") }}
        </div>
        <div v-if="isPhone" class="parts-list">
          <v-card v-for="part in historyItem.MachineServiceHistoriesParts" :key="part.Id" variant="outlined"
            class="parts-list-item">
            <div class="d-flex align-start">
              <v-avatar v-if="part.Article && part.Article.ArticleImage" size="40" class="mr-3" rounded>
                <v-img :src="part.Article.ArticleImage" />
              </v-avatar>
              <div class="flex-grow-1 min-width-0">
                <div class="text-body-2 font-weight-medium parts-item-title">
                  {{ getPartDescription(part) }}
                </div>
                <div class="text-caption parts-item-meta">
                  {{ $t("text.article_number") }}: {{ getPartNumber(part) }}
                </div>
              </div>
            </div>

            <div class="parts-list-grid mt-3">
              <div class="parts-list-field">
                <span class="parts-list-label">{{ $t("text.amount") }}</span>
                <span class="parts-list-value">{{ formatQuantity(part.ArticleQtyOrdered) }}</span>
              </div>
              <div v-if="showPrice" class="parts-list-field">
                <span class="parts-list-label">{{ $t("text.price") }}</span>
                <span class="parts-list-value">{{ formatCurrency(part.ArticlePriceOut) }}</span>
              </div>
              <div v-if="showPrice" class="parts-list-field">
                <span class="parts-list-label">{{ $t("text.total_sum") }}</span>
                <span class="parts-list-value font-weight-medium">{{ formatCurrency(part.Total) }}</span>
              </div>
            </div>
          </v-card>
        </div>

        <v-table v-else density="comfortable" hover class="parts-table">
          <thead>
            <tr>
              <th class="text-left">{{ $t("text.article") }}</th>
              <th class="text-left">{{ $t("text.article_number") }}</th>
              <th class="text-right">{{ $t("text.amount") }}</th>
              <th v-if="showPrice" class="text-right">{{ $t("text.price") }}</th>
              <th v-if="showPrice" class="text-right">{{ $t("text.total_sum") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="part in historyItem.MachineServiceHistoriesParts" :key="part.Id">
              <td>
                <div class="d-flex align-center py-2">
                  <v-avatar v-if="part.Article && part.Article.ArticleImage" size="32" class="mr-3" rounded>
                    <v-img :src="part.Article.ArticleImage" />
                  </v-avatar>
                  <span class="parts-item-title">{{ getPartDescription(part) }}</span>
                </div>
              </td>
              <td class="text-caption">{{ getPartNumber(part) }}</td>
              <td class="text-right">{{ formatQuantity(part.ArticleQtyOrdered) }}</td>
              <td v-if="showPrice" class="text-right">{{ formatCurrency(part.ArticlePriceOut) }}</td>
              <td v-if="showPrice" class="text-right font-weight-medium">{{ formatCurrency(part.Total) }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <div class="d-flex justify-end mt-4">
        <DownloadServiceReportBtn v-if="historyItem.ServiceReport || historyItem.Job" :job="historyItem.Job"
          class="mr-2" />
        <v-btn size="small" variant="outlined" color="primary" @click.stop="$emit('open', historyItem.Job)"
          style="text-transform: none;">
          <v-icon start size="small">mdi-open-in-new</v-icon>
          {{ $t("text.open") }}
        </v-btn>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script>
import DownloadServiceReportBtn from "@/components/DownloadServiceReportBtn.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";

export default {
  name: "HistoricJobCard",
  mixins: [AuthStoreMixin],
  components: {
    DownloadServiceReportBtn,
  },
  props: {
    historyItem: {
      type: Object,
      required: true,
    },
    showPrice: {
      type: Boolean,
      default: false,
    },
    isPhone: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatCurrency(value) {
      if (value == null || Number.isNaN(Number(value))) return "—";
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "NOK",
        maximumFractionDigits: 2,
      }).format(Number(value));
    },
    formatQuantity(value) {
      if (value == null || value === "") return "—";
      return Number.isNaN(Number(value)) ? value : Number(value).toLocaleString();
    },
    getPartDescription(part) {
      return part.ArticleDescription || (part.Article && part.Article.ArticleDescription) || "—";
    },
    getPartNumber(part) {
      return (part.Article && part.Article.ArticleNumber) || "—";
    },
    hasCostData(item) {
      return (
        item.KPIWorkCost != null ||
        item.KPIPartCost != null ||
        item.KPIDrivingCost != null
      );
    },
  },
};
</script>

<style scoped>
.comment-text {
  background-color: #f9f9fb;
  padding: 8px 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.history-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  overflow: hidden;
}

.history-panel::before {
  box-shadow: none !important;
}

.history-panel-title {
  min-height: 74px;
}

.history-panel-title :deep(.v-expansion-panel-title__overlay) {
  background: transparent;
}

.history-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 24px 16px;
}

.date-block {
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 6px;
  border-left: 3px solid var(--v-primary-base);
}

.cost-card {
  min-width: 100px;
  border-radius: 8px !important;
}

.parts-table {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.parts-table :deep(table) {
  background: #fff;
}

.parts-table thead th {
  background-color: #f5f7fa !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.parts-table tbody tr:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.parts-item-title {
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.35;
}

.parts-item-meta {
  color: rgba(0, 0, 0, 0.54);
}

.parts-list {
  display: grid;
  gap: 12px;
}

.parts-list-item {
  padding: 14px;
  border-radius: 10px !important;
}

.parts-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 12px;
}

.parts-list-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.parts-list-label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.parts-list-value {
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.min-width-0 {
  min-width: 0;
}
</style>
